import React from 'react';
import { createRoot } from 'react-dom/client';
import branch from 'branch-sdk';
import { isMobileDeviceDetectRegex } from './Validations';
import { getUtmParameters } from './utmTracker';
import { BRANCHIO_FEATURE_NAME,BRANCHIO_REDIRECTION_PATH_NAME } from './constant';
import BranchIoLoader from '../components/BranchIO/BranchIoLoader';

// ─── React-rendered full-screen loader ───────────────────────────────────────
const LOADER_CONTAINER_ID = 'branch-io-loader-root';
let _loaderRoot = null;

const showSpinnerOverlay = () => {
  if (document.getElementById(LOADER_CONTAINER_ID)) return;

  const container = document.createElement('div');
  container.id = LOADER_CONTAINER_ID;
  document.body.appendChild(container);

  _loaderRoot = createRoot(container);
  _loaderRoot.render(React.createElement(BranchIoLoader));
};

const hideSpinnerOverlay = () => {
  const container = document.getElementById(LOADER_CONTAINER_ID);
  if (!container) return;

  if (_loaderRoot) {
    _loaderRoot.unmount();
    _loaderRoot = null;
  }
  container.remove();
};
// ─────────────────────────────────────────────────────────────────────────────

// Use localStorage instead of module variable for redirect state
const REDIRECT_KEY = 'branchRedirectInProgress';
const REDIRECT_TIMESTAMP_KEY = 'branchRedirectTimestamp';

// Check if currently redirecting (with timeout check)
const isCurrentlyRedirecting = () => {
  const redirecting = localStorage.getItem(REDIRECT_KEY);
  const timestamp = localStorage.getItem(REDIRECT_TIMESTAMP_KEY);
  
  if (!redirecting || !timestamp) {
    return false;
  }
  
  // If more than 10 seconds have passed, consider it stale
  const now = Date.now();
  const redirectTime = parseInt(timestamp);
  if (now - redirectTime > 10000) {
    localStorage.removeItem(REDIRECT_KEY);
    localStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
    return false;
  }
  
  return true;
};

// Set redirecting state
const setRedirecting = (isRedirecting) => {
  if (isRedirecting) {
    localStorage.setItem(REDIRECT_KEY, 'true');
    localStorage.setItem(REDIRECT_TIMESTAMP_KEY, Date.now().toString());
  } else {
    localStorage.removeItem(REDIRECT_KEY);
    localStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
  }
};

// Initialize Branch SDK and reset state
export const initBranchIo = () => {
  const branchKey = process.env.REACT_APP_BRANCH_IO_KEY;
  branch.init(branchKey);
  // Reset any stale redirect state
  setRedirecting(false);
};

// Reset the redirecting flag (useful for clearing the state)
export const resetRedirectingFlag = () => {
  setRedirecting(false);
};

// Generate Branch.io deep link and redirect user
export const generateBranchLinkAndRedirect = (routePath, options = {}) => {
  if (isCurrentlyRedirecting() || !isMobileDeviceDetectRegex) {
    return false;
  }

  setRedirecting(true);
  showSpinnerOverlay();
  const utmParams = getUtmParameters();

  const linkData = {
    channel: 'web',
    feature: options.feature || BRANCHIO_FEATURE_NAME.REDIRECT_INTENDED_PAGE,
    data: {
      path: routePath,
      $desktop_url: process.env.REACT_APP_DOMAIN_URL,
      ...utmParams,
    }
  };

  branch.link(linkData, (err, link) => { 
    if (!err && link) {
      window.location.href = link;
      // Note: redirect state will be cleared on next page load
    } else {
      hideSpinnerOverlay();
      setRedirecting(false);
    }
  });

  return true;
};

// Main function to handle mobile redirection for specific routes
export const handleMobileRedirection = (currentPath, isUserAction = false) => {
  // Check if we're already in the middle of a redirect
  if (isCurrentlyRedirecting()) {
    return;
  }

  // Only redirect for mobile devices
  if (!isMobileDeviceDetectRegex) {
    return;
  }

  // List of routes that should trigger mobile redirection
  const mobileRedirectRoutes = [
    '/',
    '/portfolio',
    '/account',
    '/account/transactions',
    '/account/payment',
    '/account/settings',
    '/account/help-center',
    '/account/documents',
    '/account/legal',
    '/account/affiliate-program',
    '/portfolio/assets',
    '/login',
    '/sign-upAuth',
  ];

  const shouldRedirect = mobileRedirectRoutes.some(route =>
    currentPath === route || currentPath.startsWith(`${route}/`)
  );

  if (shouldRedirect) {
    let feature = BRANCHIO_FEATURE_NAME.REDIRECT_INTENDED_PAGE;
    if (isUserAction) {
      if (currentPath === BRANCHIO_REDIRECTION_PATH_NAME.LOGIN) feature = BRANCHIO_FEATURE_NAME.LOGIN;
      if (currentPath === BRANCHIO_REDIRECTION_PATH_NAME.SIGN_UP_AUTH) feature = BRANCHIO_FEATURE_NAME.SIGN_UP;
      if (currentPath === BRANCHIO_REDIRECTION_PATH_NAME.HOMEPAGE) feature = BRANCHIO_FEATURE_NAME.HOMEPAGE;
    } else {
      if (currentPath === BRANCHIO_REDIRECTION_PATH_NAME.HOMEPAGE) feature = BRANCHIO_FEATURE_NAME.HOMEPAGE;
    }
    generateBranchLinkAndRedirect(currentPath, { feature });
  }
};